/**
 * Pure logic for the "Review against protocol" panel in the CRF Builder.
 * The review only flags; nothing here blocks approval. Copy is plain language
 * for a data manager (contract: review_contract.md, 2026-10-06).
 */

export type ReviewKind =
  | 'missing_field'
  | 'contradicts_protocol'
  | 'not_in_protocol'
  | 'wrong_options'
  | 'empty_options'
  | 'duplicate'
  | 'form_not_supported';

export interface ReviewFinding {
  id: string;
  kind: string;
  fieldVarName: string | null;
  message: string;
  protocolSection: string | null;
  protocolQuote: string | null;
  quoteVerified: boolean;
  /** True when the cited section appears in the protocol; absent on older results. */
  sectionVerified?: boolean;
  /** 2 when both checks found it; 1 or absent otherwise. */
  foundInPasses?: number;
}

export interface ReviewFormDoc {
  runId: string;
  status: 'reviewed' | 'failed' | 'skipped' | string;
  reason: string | null;
  findings: ReviewFinding[];
  /** Plain-text note about the checks, e.g. the second check did not finish. */
  passNote?: string | null;
}

export interface ReviewSummaryLike {
  status: 'running' | 'complete' | 'failed' | string;
  formsReviewed: number;
  formsTotal: number;
  findingCount: number;
  error: string | null;
  /** Set by the server on a partial run. */
  formsRemaining?: number;
}

/** Forms still to review on a partial run. */
export function remainingForms(s: ReviewSummaryLike): number {
  if (typeof s.formsRemaining === 'number') return Math.max(0, s.formsRemaining);
  return Math.max(0, s.formsTotal - s.formsReviewed);
}

export function continueLabel(s: ReviewSummaryLike): string {
  const m = remainingForms(s);
  return `Continue review (${m} ${m === 1 ? 'form' : 'forms'} left)`;
}

export interface MissingFormLike {
  name: string;
  reason: string;
  protocolSection: string | null;
}

export const UNVERIFIED_QUOTE_LABEL = 'quoted text not found in the protocol';

const KIND_ORDER: string[] = [
  'missing_field', 'contradicts_protocol', 'wrong_options', 'empty_options',
  'duplicate', 'not_in_protocol', 'form_not_supported',
];

const KIND_LABELS: Record<string, string> = {
  missing_field: 'Missing from the form',
  contradicts_protocol: 'Disagrees with the protocol',
  not_in_protocol: 'Not in the protocol',
  wrong_options: 'Answer options differ from the protocol',
  empty_options: 'No answer options',
  duplicate: 'Collected twice',
  form_not_supported: 'Form not supported by the protocol',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? 'Other';
}

/** The label applies only to a non-empty quote that was not found. */
export function isUnverifiedQuote(x: { protocolQuote?: string | null; quoteVerified: boolean }): boolean {
  return typeof x.protocolQuote === 'string' && x.protocolQuote.trim() !== '' && !x.quoteVerified;
}

export const UNVERIFIED_SECTION_LABEL = 'section not found in the protocol';

/** A cited section the backend could not find in the protocol. */
export function isUnverifiedSection(x: { protocolSection?: string | null; sectionVerified?: boolean }): boolean {
  return typeof x.protocolSection === 'string' && x.protocolSection.trim() !== '' && x.sectionVerified === false;
}

/** Findings with an unverified quote or section go last; stable inside each half. Does not mutate. */
export const FOUND_BY_BOTH_LABEL = 'found by both checks';

export function isFoundByBoth(x: { foundInPasses?: number }): boolean {
  return x.foundInPasses === 2;
}

export function sortFindings<T extends { foundInPasses?: number; protocolQuote?: string | null; quoteVerified: boolean; protocolSection?: string | null; sectionVerified?: boolean }>(findings: T[]): T[] {
  const weak = (x: T) => isUnverifiedQuote(x) || isUnverifiedSection(x);
  const strong = findings.filter((x) => !weak(x));
  return [...strong.filter(isFoundByBoth), ...strong.filter((x) => !isFoundByBoth(x)), ...findings.filter(weak)];
}

export const STALE_RUN_TEXT = 'The last review did not finish. You can run it again.';
const STALE_RUN_MS = 31 * 60 * 1000;

/** A "running" summary older than the server's longest run never finished. */
export function isStaleRunning(s: { status: string; startedAt?: unknown; claimedAt?: unknown }, now: Date): boolean {
  if (s.status !== 'running') return false;
  const started = toDateValue(s.claimedAt) ?? toDateValue(s.startedAt);
  return !!started && now.getTime() - started.getTime() > STALE_RUN_MS;
}

/** ISO string (as stored) or Firestore Timestamp to a Date; null when unusable. */
export function toDateValue(v: unknown): Date | null {
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (v && typeof v === 'object' && typeof (v as { toDate?: unknown }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return null;
}

/** Items and forms for the banner: only docs for current forms, from this run. */
export function currentReviewTotals(
  docs: Record<string, ReviewFormDoc>,
  runId: string | undefined,
  currentFormIds: string[],
): { items: number; forms: number; notReviewed: number } {
  let items = 0;
  let forms = 0;
  let notReviewed = 0;
  for (const id of currentFormIds) {
    const d = docs[id];
    if (d && d.runId === runId && (d.status === 'failed' || d.status === 'skipped')) notReviewed += 1;
    if (!d || d.runId !== runId || d.status !== 'reviewed' || d.findings.length === 0) continue;
    items += d.findings.length;
    forms += 1;
  }
  return { items, forms, notReviewed };
}

export function groupFindings(findings: ReviewFinding[]): Array<{ kind: string; label: string; findings: ReviewFinding[] }> {
  const byKind = new Map<string, ReviewFinding[]>();
  for (const x of findings) {
    const list = byKind.get(x.kind) ?? [];
    list.push(x);
    byKind.set(x.kind, list);
  }
  const kinds = [...byKind.keys()].sort((a, b) => {
    const ia = KIND_ORDER.indexOf(a); const ib = KIND_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  return kinds.map((kind) => ({ kind, label: kindLabel(kind), findings: sortFindings(byKind.get(kind)!) }));
}

export interface FormBadge {
  text: string;
  tone: 'amber' | 'red' | 'gray';
  stale: boolean;
  title: string | undefined;
}

export function formBadge(doc: ReviewFormDoc | undefined, summaryRunId: string | undefined): FormBadge | null {
  if (!doc) return null;
  const stale = !!summaryRunId && doc.runId !== summaryRunId;
  const suffix = stale ? ' (from an earlier review)' : '';
  if (doc.status === 'failed') {
    return { text: 'Review failed' + suffix, tone: 'red', stale, title: doc.reason ?? undefined };
  }
  if (doc.status === 'skipped') {
    return { text: 'Not reviewed' + suffix, tone: 'gray', stale, title: doc.reason ?? undefined };
  }
  if (doc.status !== 'reviewed') return null;
  const n = doc.findings.length;
  if (n === 0) {
    return doc.passNote ? { text: 'Checked once' + suffix, tone: 'gray', stale, title: doc.passNote } : null;
  }
  return { text: `${n} ${n === 1 ? 'item' : 'items'} to check${suffix}`, tone: 'amber', stale, title: undefined };
}

export function bannerText(s: ReviewSummaryLike, formsWithFindings: number, lastRun: string, notReviewed = 0): string {
  if (s.status === 'running') return `Reviewing ${s.formsReviewed} of ${s.formsTotal} forms against the protocol...`;
  if (s.status === 'failed') return s.error || 'The protocol review did not finish.';
  if (s.status === 'partial') {
    const m = remainingForms(s);
    return `Protocol review stopped before all forms were done: ${s.findingCount} ${s.findingCount === 1 ? 'item' : 'items'} to check so far; ${m} ${m === 1 ? 'form' : 'forms'} still to review.`;
  }
  const missed = notReviewed > 0 ? `${notReviewed} ${notReviewed === 1 ? 'form' : 'forms'} could not be reviewed` : '';
  if (s.findingCount === 0) {
    return missed
      ? `No items found on the reviewed forms; ${missed} (last run ${lastRun})`
      : `Protocol review: nothing to check (last run ${lastRun})`;
  }
  const items = s.findingCount === 1 ? 'item' : 'items';
  const forms = formsWithFindings === 1 ? 'form' : 'forms';
  return `Protocol review: ${s.findingCount} ${items} to check across ${formsWithFindings} ${forms}${missed ? `; ${missed}` : ''} (last run ${lastRun})`;
}

export function missingFormLine(m: MissingFormLike): string {
  return `${m.name} - ${m.reason}${m.protocolSection ? ` (protocol section ${m.protocolSection})` : ''}`;
}

/** A client-side deadline or timeout is not a failure: the server keeps working. */
export function isStillRunningError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code : '';
  const message = typeof e.message === 'string' ? e.message : '';
  return /deadline-exceeded/i.test(code) || /deadline[- ]exceeded|timed? ?out|timeout/i.test(message);
}
